<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import axios from 'axios'
import { usePage } from '@inertiajs/vue3'
import { useForm } from 'laravel-precognition-vue-inertia'
import { trans } from 'laravel-vue-i18n'
import Button from 'primevue/button'
import ReservationWidget from '../../../views/contacts/log-widget/ReservationWidget.vue'

const page = usePage()

const props = defineProps({
    initialMessages: { type: Array, default: () => [] },
    initialHasMore: { type: Boolean, default: false },
    initialNextPage: { type: Number, default: 2 },
    contactId: { type: Number, required: true },
    contact: Object,
    creditAmount: { type: Number, default: 0 },
})

const messages = ref([...props.initialMessages])
const hasMore = ref(props.initialHasMore)
const nextPage = ref(props.initialNextPage)
const isLoading = ref(false)
const sentinel = ref(null)

async function loadMore() {
    if (isLoading.value || !hasMore.value) return
    isLoading.value = true
    try {
        const { data } = await axios.get(`/app/contacts/${props.contactId}/log-messages`, {
            params: { page: nextPage.value },
        })
        messages.value.push(...data.data)
        hasMore.value = data.current_page < data.last_page
        nextPage.value = data.current_page + 1
    } finally {
        isLoading.value = false
    }
}

const observer = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) loadMore()
}, { threshold: 0.1 })

watch(() => props.initialMessages, (newMessages) => {
    if (!newMessages?.length) return
    const newIds = new Set(newMessages.map((m) => m.id))
    const olderMessages = messages.value.filter((m) => !newIds.has(m.id))
    messages.value = [...newMessages, ...olderMessages]
})

onMounted(() => { if (sentinel.value) observer.observe(sentinel.value) })
onUnmounted(() => observer.disconnect())

const MESSAGE_TYPES = ['sent_sms_message', 'sent_email_message', 'received_message']
const SYSTEM_TYPES = ['new_reservation', 'reservation_change', 'reservation_remove', 'reservation_change_request']

const dayFormatter = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'long' })
const dayFormatterWithYear = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' })

function dayLabel(dateStr) {
    const date = new Date(dateStr)
    const isCurrentYear = date.getFullYear() === new Date().getFullYear()
    return (isCurrentYear ? dayFormatter : dayFormatterWithYear).format(date)
}

// ── Chronologický feed s vloženými day-markery (nejnovější zprávy nahoře, dle origin_date desc z backendu) ──
const feedItems = computed(() => {
    const items = []
    let lastDay = null
    for (const message of messages.value) {
        const day = message.origin_date ? new Date(message.origin_date).toDateString() : null
        if (day && day !== lastDay) {
            items.push({ kind: 'day', key: `day_${day}`, label: dayLabel(message.origin_date) })
            lastDay = day
        }
        items.push({ kind: 'message', key: `msg_${message.id}`, message })
    }
    return items
})

function isChatMessage(message) {
    return MESSAGE_TYPES.includes(message.type)
}

function isOutgoing(message) {
    return message.origin !== 'contact'
}

function channelLabel(message) {
    return message.type === 'sent_sms_message' ? trans('contacts.channel_sms') : trans('contacts.channel_email')
}

const getMessageLabel = (message) => {
    switch (message.type) {
        case 'new_reservation':
            return trans('contacts.activity_new_reservation')
        case 'reservation_change':
            return trans('contacts.activity_change_reservation')
        case 'reservation_confirm':
            return trans('contacts.activity_confirm_reservation')
        case 'reservation_remove':
            return trans('contacts.activity_remove_reservation')
        case 'reservation_change_request':
            if (message.reservation_change_requests?.action === 'change') {
                return trans('contacts.activity_change_request')
            }
            return trans('contacts.activity_cancel_request')
        default:
            return ''
    }
}

const systemIcon = (type) => {
    switch (type) {
        case 'new_reservation':
            return 'pi pi-plus-circle'
        case 'reservation_change':
        case 'reservation_change_request':
            return 'pi pi-arrow-right-arrow-left'
        case 'reservation_remove':
            return 'pi pi-times-circle'
        default:
            return 'pi pi-bolt'
    }
}

// ── Compose ─────────────────────────────────────────────────────

const contactForm = useForm('post', `/app/contacts/${props.contactId}/send-message`, {
    message: '',
    sendAs: '',
})

function sendEmail() {
    contactForm.sendAs = 'email'
    contactForm.submit()
    contactForm.message = ''
}

function sendSMS() {
    contactForm.sendAs = 'sms'
    contactForm.submit()
    contactForm.message = ''
}
</script>

<template>
    <div class="flex flex-col h-full min-h-0">
        <div class="flex-1 min-h-0 overflow-y-auto px-3 md:px-4 py-3 flex flex-col gap-3.5">
            <div
                v-if="feedItems.length === 0"
                class="flex flex-col items-center justify-center py-10 opacity-60 text-sm"
            >
                <i class="pi pi-inbox text-3xl mb-2"></i>
                <p>{{ $t('contacts.no_records') }}</p>
            </div>

            <template v-for="item in feedItems" :key="item.key">
                <div v-if="item.kind === 'day'" class="flex items-center gap-2.5 my-1">
                    <div class="flex-1 h-px bg-neutral-200 dark:bg-gray-700"></div>
                    <span class="text-[10.5px] font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 whitespace-nowrap">{{ item.label }}</span>
                    <div class="flex-1 h-px bg-neutral-200 dark:bg-gray-700"></div>
                </div>

                <!-- systémová událost -->
                <div v-else-if="!isChatMessage(item.message)" class="flex gap-2.5 items-start">
                    <div class="w-[26px] h-[26px] rounded-full bg-neutral-50 dark:bg-gray-800 border border-neutral-200 dark:border-gray-700 flex items-center justify-center text-neutral-500 dark:text-neutral-400 flex-shrink-0 mt-0.5">
                        <i :class="[systemIcon(item.message.type), '!text-[13px]']"></i>
                    </div>
                    <div class="flex-1 min-w-0 pt-0.5">
                        <p class="text-[13px] font-semibold text-neutral-800 dark:text-neutral-100">
                            {{ getMessageLabel(item.message) }}
                            <span
                                v-if="['reservation_change_request'].includes(item.message.type) && item.message.reservation_change_requests?.action === 'change'"
                                class="font-normal text-neutral-500 dark:text-neutral-400"
                            >
                                {{ $t('contacts.log_change_on') }}
                                <span class="border rounded-full bg-white p-1 dark:border-neutral-700 dark:bg-black text-xs">{{ item.message.reservation_change_requests.new_time_start?.substring(0, 5) }} - {{ item.message.reservation_change_requests.new_time_end?.substring(0, 5) }}</span>
                                {{ $t('common.and') }}
                                <span class="border rounded-full bg-white p-1 dark:border-neutral-700 dark:bg-black text-xs">{{ item.message.reservation_change_requests.formatted_date }}</span>
                            </span>
                        </p>
                        <p class="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">{{ item.message.formatted_date }}</p>
                        <ReservationWidget
                            v-if="['reservation_change', 'reservation_remove', 'new_reservation'].includes(item.message.type)"
                            :message="item.message"
                        />
                    </div>
                </div>

                <!-- chat bublina -->
                <div
                    v-else
                    class="flex flex-col gap-1 max-w-[84%]"
                    :class="isOutgoing(item.message) ? 'self-end items-end' : 'self-start items-start'"
                >
                    <div
                        class="px-3.5 py-2.5 text-[13.5px] leading-relaxed"
                        :class="isOutgoing(item.message)
                            ? 'bg-roori-blue text-white rounded-2xl rounded-br-md'
                            : 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-100 rounded-2xl rounded-bl-md'"
                        v-html="item.message.message"
                    ></div>
                    <div class="flex items-center gap-1.5 text-[10.5px] text-neutral-400 dark:text-neutral-500 font-medium px-0.5">
                        <span v-if="isOutgoing(item.message)" class="font-semibold text-neutral-500 dark:text-neutral-400">{{ channelLabel(item.message) }} ·</span>
                        {{ item.message.formatted_date }}
                    </div>
                </div>
            </template>

            <div ref="sentinel" class="h-1" />

            <div v-if="isLoading" class="flex justify-center py-4">
                <i class="pi pi-spinner animate-spin text-neutral-400" />
            </div>
            <div v-else-if="!hasMore && messages.length > 0" class="text-center text-[11px] text-neutral-400 py-2">
                {{ $t('contacts.all_loaded') }}
            </div>
        </div>

        <div class="shrink-0 bg-white dark:bg-gray-900 border-t border-neutral-200 dark:border-gray-700 px-3 md:px-4 py-2.5">
            <div class="border border-neutral-200 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-900 overflow-hidden focus-within:ring-2 focus-within:ring-roori-blue/20 focus-within:border-roori-blue">
                <textarea
                    v-model="contactForm.message"
                    class="w-full resize-none outline-none px-3.5 pt-2.5 pb-1.5 text-[13.5px] bg-transparent"
                    :placeholder="$t('contacts.message_placeholder')"
                    rows="2"
                ></textarea>
                <small v-if="contactForm.errors['message']" class="block px-3.5 text-xs text-red-600">{{ contactForm.errors['message'] }}</small>
                <div class="flex justify-end gap-2 px-2.5 pb-2 pt-1">
                    <Button size="small" class="!px-3.5 !rounded-lg" severity="secondary" outlined icon="pi pi-send" :label="$t('contacts.send_as_email')" @click="sendEmail" />
                    <Button
                        v-if="contact.phone"
                        size="small"
                        class="!px-3.5 !rounded-lg !bg-roori-blue !border-roori-blue"
                        icon="pi pi-mobile"
                        :disabled="creditAmount === 0"
                        v-tooltip.bottom="creditAmount === 0 ? $t('contacts.credits_warning') : null"
                        :label="$t('contacts.send_as_sms')"
                        @click="sendSMS"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
